import type { ClusterDetailsTab } from "@/components/cloud-clusters/cloud-cluster-tabs.constants";
import type {
  RouterAccessTab,
  RouterConnectionDetailTab,
  RouterKeyDetailTab,
  RouterRouteDetailTab,
  RouterRoutingTab,
} from "@/components/router/router-tabs.constants";
import { getFeatureTableV3QueryParamNamespace } from "@/components/feature/FeatureTableV3.constants";
import { IntegrationDetailTab } from "@/components/integration/integration-tabs.constants";
import { resolverSourceToSourceQuery } from "@/components/source/sourceUtils";
import { withBasicTableV3FilterQueryParam } from "@/components/table/v3/BasicTableV3/BasicTableV3.serverFilters";
import {
  getTestComponentPaneHref,
  TEST_COMPONENTS_ROUTE_CONFIG,
} from "@/components/test-page/test-components-config";
import {
  DynamicQueryParam,
  StaticQueryParam,
  TILES_DRAWER_QUERY_NAMESPACE,
} from "@/constants/query-params";
import { isNonEmptyArray } from "@/utils/array-utils";
import { fastCompact } from "@/utils/object-utils";
import { encodeRouteParam } from "@/utils/url-utils";
type ID = string | number;

export type CaseStudyName =
  | "vital"
  | "verisoul"
  | "apartment-list"
  | "moneylion"
  | "mission-lane"
  | "whatnot"
  | "iwoca"
  | "fis"
  | "medely"
  | "turo"
  | "melio";

// Fields that are important to routing to the right resolver/feature definition
export interface RouteContext {
  environmentId: string | undefined;
  projectId: string | undefined;
  branch: string | undefined;
  deployment: string | undefined;
}

const appendViewQueryParam = (path: string, tab?: string): string => {
  if (!tab) {
    return path;
  }

  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${DynamicQueryParam.VIEW}=${encodeRouteParam(tab)}`;
};

const appendFeatureNamespaceFilter = (
  href: string,
  namespace: ID | undefined,
  queryStateNamespace?: string,
): string =>
  namespace == null
    ? href
    : withBasicTableV3FilterQueryParam({
        filter: { operator: "is any of", value: String(namespace) },
        href,
        queryParam: DynamicQueryParam.NAMESPACES,
        queryParamNamespace: getFeatureTableV3QueryParamNamespace(queryStateNamespace),
      });

export const Routes = {
  home: "/",
  login: "/login",
  cliAuthError: "/cli/error",
  loginNoTeam: "/login/no-organization",
  signup: "/signup",
  profile: "/profile",

  onboarding: {
    signupCode: "/onboarding/signup-code",
    createTeam: "/onboarding/create-team",
    createEnvironment: "/onboarding/create-environment",
  },
  signupLink: {
    claim: "/signup/link/claim",
    landing: (publicId: string) => `/signup/link/${encodeRouteParam(publicId)}`,
  },

  join: {
    /** Landing page for an invite link. Keep in sync with `signupCodeInviteUrl`. */
    invite: (code: string) => `/join/${encodeRouteParam(code)}`,
    /** Where a visitor lands after signing in from an invite link. */
    claim: "/join/claim",
  },

  blog: "/blog",
  blogPost: (slug: string) => `/blog/${encodeRouteParam(slug)}`,

  register: (slug: string) => `/register/${encodeRouteParam(slug)}`,
  watch: (slug: string) => `/watch/${encodeRouteParam(slug)}`,

  customers: `/customers`,
  customer: (customerName: CaseStudyName) => `/customers/${encodeRouteParam(customerName)}`,

  // logical link to "home" of dashboard
  docs: `https://docs.chalk.ai/docs/what-is-chalk`,
  docsCli: "https://docs.chalk.ai/cli",
  docsSdk: "https://docs.chalk.ai/api-docs",
  docsServerless: "https://docs.chalk.ai/docs/serverless",
  changelog: `https://docs.chalk.ai/docs/changelog`,
  contact: `mailto:hello@chalk.ai`,
  pricing: "/pricing",
  careers: `/careers`,
  dashboard: `/projects`,
  projects: `/projects`,
  default: `/projects?v=default`,
  newProject: `/projects/new`,

  bookDemo: `/book-demo`,
  contactUsThankYou: `/thank-you`,

  cloudVpcs: `/cloud-vpcs`,
  cloudVpc: (vpcId: ID) => `/cloud-vpcs?vpcId=${encodeRouteParam(vpcId)}`,
  cloudOverview: `/cloud-overview`,
  cloudClusters: `/cloud-clusters`,
  newCloudCluster: `/cloud-clusters/new`,
  cloudCluster: (clusterId: ID, tab?: ClusterDetailsTab) =>
    appendViewQueryParam(`/cloud-clusters/${encodeRouteParam(clusterId)}`, tab),
  cloudClusterHostCapacity: (clusterId: ID) =>
    `/cloud-clusters/${encodeRouteParam(clusterId)}/host-capacity`,
  cloudClusterGateway: (clusterId: ID, gatewayId: ID) =>
    `${appendViewQueryParam(
      `/cloud-clusters/${encodeRouteParam(clusterId)}`,
      "gateway",
    )}&gatewayId=${encodeRouteParam(gatewayId)}`,
  cloudClusterTelemetry: (clusterId: ID, telemetryId: ID) =>
    `${appendViewQueryParam(
      `/cloud-clusters/${encodeRouteParam(clusterId)}`,
      "telemetry",
    )}&telemetryId=${encodeRouteParam(telemetryId)}`,
  cloudClusterClickhouse: (clusterId: ID, telemetryId?: ID) =>
    `/cloud-clusters/${encodeRouteParam(clusterId)}/clickhouse${
      telemetryId == null ? "" : `?telemetryId=${encodeRouteParam(telemetryId)}`
    }`,
  cloudClusterEdit: (clusterId: ID) => `/cloud-clusters/${encodeRouteParam(clusterId)}/edit`,
  cloudContainerRegistries: `/cloud-container-registries`,
  newCloudContainerRegistry: `/cloud-container-registries/new`,
  cloudContainerRegistry: (registryId: ID) =>
    `/cloud-container-registries/${encodeRouteParam(registryId)}`,
  adminNodes: `/admin-nodes`,
  adminNodeDetail: (cluster: string, node: string, nodeUid?: string) =>
    `/admin-nodes/${encodeRouteParam(cluster)}/${encodeRouteParam(node)}${
      nodeUid ? `?nodeUid=${encodeRouteParam(nodeUid)}` : ""
    }`,
  cloudStorage: `/cloud-storage`,
  cloudStorageDetail: (storageId: ID) => `/cloud-storage/${encodeRouteParam(storageId)}`,
  newCloudStorage: `/cloud-storage/new`,
  cloudAccounts: `/cloud-accounts`,
  cloudAccount: (credentialId: ID) =>
    `/cloud-accounts?credentialId=${encodeRouteParam(credentialId)}`,
  newCloudAccount: `/cloud-accounts/new`,

  about: {
    company: "/about",
    security: "https://docs.chalk.ai/docs/security",
    status: "https://chalk.statuspage.io/",
    press: "https://chalk-ai.notion.site/chalk-brand-kit",
  },
  legal: {
    subprocessors: "/legal/subprocessors",
    privacy: "/legal/privacy",
    websiteTerms: "/legal/website-terms",
    termsOfUse: "/legal/terms-of-use",
    servicesAgreement: "/legal/services-agreement",
    dataProcessingAddendum: "/legal/data-processing-addendum",
  },
  social: {
    twitter: "https://twitter.com/chalk",
    linkedin: "https://www.linkedin.com/company/chalkai/",
    angelist: "https://angel.co/chalk-ai",
    github: "https://github.com/chalk-ai",
    youtube: "https://www.youtube.com/@chalk-ai",
  },
  solutions: {
    credit: "/solutions/credit",
    fraud: "/solutions/fraud",
    maintenance: "/solutions/maintenance",
    marketplaces: "/solutions/marketplaces",
    insurance: "/solutions/insurance",
    personalization: "/solutions/personalization",
  },
  useCases: {
    fraudDetection: "/use-case/identity-fraud",
    payments: "/use-case/payments",
    underWriting: "/use-case/underwriting",
    recommenderSystems: "/use-case/recommender-systems",
    searchAndRanking: "/use-case/search-and-ranking",
    growthDecisioning: "/use-case/growth-decisioning",
  },
  products: {
    featureStore: "/products/feature-store",
    monitor: "/products/monitoring",
    compute: "/products/compute",
    branches: "/products/branches",
    llm: "/products/llm",
  },
  productsCMS: {
    featureStore: "/capabilities/feature-store",
    realTimeServing: "/capabilities/real-time-serving",
    temporalAggregations: "/capabilities/temporal-aggregations",
    trainingData: "/capabilities/training-data",
    llmToolchain: "/capabilities/llm-toolchain",
  },
  examples: "/code-examples",
  ddogTrace: (traceId: string) =>
    `https://app.datadoghq.com/apm/trace/${encodeRouteParam(traceId)}`,
  testComponents: {
    root: TEST_COMPONENTS_ROUTE_CONFIG.path,
    // Link to a specific test-components pane on the static harness route.
    // The dedicated `pane` param leaves `?v=` to in-pane tab viewers.
    pane: (paneId: string) => getTestComponentPaneHref(paneId),
  },
  zipViewer: () => getTestComponentPaneHref("metadata-zip"),
  global: {
    cloudAccounts: `/cloud-accounts`,
    cloudOverview: `/cloud-overview`,
    siteOverview: `/site-overview`,
    migrationStatusList: `/migration-status`,
    migrationStatus: (migrationId: ID) => `/migration-status/${encodeRouteParam(migrationId)}`,
    signupCodes: `/signup-codes`,
    signupLinks: `/signup-links`,
    licenseKeys: `/license-keys`,
    cloudVpcs: `/cloud-vpcs`,
    cloudClusters: `/cloud-clusters`,
    cloudContainerRegistries: `/cloud-container-registries`,
    cloudStorage: `/cloud-storage`,
    recoveryClusters: `/recovery-clusters`,
  },
  team: {
    auditLogs: "/settings/audit-logs",
    cloudAccounts: (teamId: ID) => `/teams/${encodeRouteParam(teamId)}/cloud-accounts`,
    cloudOverview: (teamId: ID) => `/teams/${encodeRouteParam(teamId)}/cloud-overview`,
    cloudVpcs: (teamId: ID) => `/teams/${encodeRouteParam(teamId)}/cloud-vpcs`,
    cloudClusters: (teamId: ID) => `/teams/${encodeRouteParam(teamId)}/cloud-clusters`,
    cloudContainerRegistries: (teamId: ID) =>
      `/teams/${encodeRouteParam(teamId)}/cloud-container-registries`,
    cloudStorage: (teamId: ID) => `/teams/${encodeRouteParam(teamId)}/cloud-storage`,
    settings: (view = "permissions", tab?: string | undefined) =>
      appendViewQueryParam(`/settings/${encodeRouteParam(view)}`, tab),
  },
  project: {
    index: (proj: ID) => `/projects/${encodeRouteParam(proj)}`,
    createEnvironment: (proj: ID) => `/projects/${encodeRouteParam(proj)}/create-environment`,

    environment: {
      root: (proj: ID, env: ID) =>
        `${Routes.project.index(proj)}/environments/${encodeRouteParam(env)}`,
      agentConversations: (proj: ID, env: ID) =>
        Routes.project.environment.settings(proj, env, "assistant"),
      agentConversation: (proj: ID, env: ID, conversationId: ID) =>
        `${Routes.project.environment.root(proj, env)}/agent-conversations/${encodeRouteParam(conversationId)}`,
      agentConversationArtifacts: (proj: ID, env: ID, conversationId: ID) =>
        `${Routes.project.environment.agentConversation(proj, env, conversationId)}/artifacts`,
      dashboard: (proj: ID, env: ID) => Routes.project.environment.root(proj, env),
      resolvers: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/resolvers`,
      graphViz: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/resolvers/graph-viz`,
      protoGraph: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/resolvers/proto`,
      resolver: (proj: ID, env: ID, fqnOrShortname: ID, tab?: string) =>
        appendViewQueryParam(
          `${Routes.project.environment.resolvers(proj, env)}/${encodeRouteParam(fqnOrShortname)}`,
          tab,
        ),
      cronQuery: (proj: ID, env: ID, cronQueryId: ID) =>
        `${Routes.project.environment.root(proj, env)}/scheduled-queries/${encodeRouteParam(cronQueryId)}`,
      cronQueryByName: (proj: ID, env: ID, cronQueryName: ID) =>
        `${Routes.project.environment.root(
          proj,
          env,
        )}/scheduled-queries-by-name/${encodeRouteParam(cronQueryName)}`,
      runs: (proj: ID, env: ID, tab?: string) =>
        appendViewQueryParam(`${Routes.project.environment.root(proj, env)}/runs`, tab),
      run: (proj: ID, env: ID, runId: ID) =>
        `${Routes.project.environment.runs(proj, env)}/${encodeRouteParam(runId)}`,
      deployments: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/deployments`,
      deployment: (proj: ID, env: ID, deploymentId: ID) =>
        `${Routes.project.environment.deployments(proj, env)}/${encodeRouteParam(deploymentId)}`,
      deploymentRevision: (proj: ID, env: ID, deploymentId: ID, deploymentRevisionId: ID) =>
        `${Routes.project.environment.deployment(
          proj,
          env,
          deploymentId,
        )}/revisions/${encodeRouteParam(deploymentRevisionId)}`,
      deploymentResolver: (proj: ID, env: ID, deploymentId: ID, fqnOrShortName: ID, tab?: string) =>
        appendViewQueryParam(
          `${Routes.project.environment.deployment(
            proj,
            env,
            deploymentId,
          )}/resolvers/${encodeRouteParam(fqnOrShortName)}`,
          tab,
        ),
      deploymentFeature: (proj: ID, env: ID, deploymentId: ID, fqn: ID) =>
        `${Routes.project.environment.deployment(proj, env, deploymentId)}/features/${encodeRouteParam(fqn)}`,
      deploymentFeatures: (proj: ID, env: ID, deploymentId: ID, namespace?: ID) =>
        appendFeatureNamespaceFilter(
          `${Routes.project.environment.deployment(proj, env, deploymentId)}/features`,
          namespace,
        ),
      // The source code portal reads its deployment from the `deploymentId` query
      // param (falling back to the environment's active deployment when absent).
      sourceCode: (proj: ID, env: ID, deploymentId?: ID) =>
        `${Routes.project.environment.root(proj, env)}/source-code` +
        (deploymentId != null
          ? `?${DynamicQueryParam.DEPLOYMENT_ID}=${encodeRouteParam(deploymentId)}`
          : ""),
      sourceCodeTarget: (
        proj: ID,
        env: ID,
        deploymentId: ID,
        filename: string,
        line?: number,
        extent?: number,
      ) =>
        `${Routes.project.environment.root(
          proj,
          env,
        )}/source-code?${DynamicQueryParam.DEPLOYMENT_ID}=${encodeRouteParam(deploymentId)}&${resolverSourceToSourceQuery(
          filename,
          line,
          extent,
        )}`,
      deploymentDashboard: (proj: ID, env: ID, deploymentId: ID) =>
        `${Routes.project.environment.deployments(proj, env)}/${encodeRouteParam(deploymentId)}/resolvers`,
      branches: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/branches`,
      // Branch names may contain characters like '/', so they must be encoded
      // to stay a single [branchName] path segment.
      branch: (proj: ID, env: ID, branch: ID) =>
        `${Routes.project.environment.root(proj, env)}/branches/${encodeRouteParam(branch)}`,
      branchEdit: (proj: ID, env: ID, branch: ID) =>
        `${Routes.project.environment.branch(proj, env, branch)}/edit`,
      branchDeploymentResolvers: (proj: ID, env: ID, branch: ID, branchDeploymentId: ID) =>
        `${Routes.project.environment.branch(
          proj,
          env,
          branch,
        )}/deployments/${encodeRouteParam(branchDeploymentId)}/resolvers`,
      branchDeploymentResolver: (
        proj: ID,
        env: ID,
        branch: ID,
        branchDeploymentId: ID,
        fqnOrShortname: ID,
      ): string =>
        `${Routes.project.environment.branchDeploymentResolvers(
          proj,
          env,
          branch,
          branchDeploymentId,
        )}/${encodeRouteParam(fqnOrShortname)}`,
      branchDeploymentFeature: (proj: ID, env: ID, branch: ID, branchDeploymentId: ID, fqn: ID) =>
        `${Routes.project.environment.branch(
          proj,
          env,
          branch,
        )}/deployments/${encodeRouteParam(branchDeploymentId)}/features/${encodeRouteParam(fqn)}`,
      branchDeploymentFeatures: (
        proj: ID,
        env: ID,
        branchName: ID,
        branchDeploymentId: ID,
        namespace?: ID,
      ) => {
        const featuresHref = `${Routes.project.environment.branchDeployment(
          proj,
          env,
          branchName,
          branchDeploymentId,
        )}?${DynamicQueryParam.VIEW}=features`;

        return appendFeatureNamespaceFilter(featuresHref, namespace, "branch-deployment-features");
      },
      branchDeployment: (proj: ID, env: ID, branchName: ID, branchDeploymentId: ID) =>
        `${Routes.project.environment.branch(
          proj,
          env,
          branchName,
        )}/deployments/${encodeRouteParam(branchDeploymentId)}`,
      features: (proj: ID, env: ID, namespace?: ID) =>
        appendFeatureNamespaceFilter(
          `${Routes.project.environment.root(proj, env)}/features`,
          namespace,
        ),
      feature: (proj: ID, env: ID, fqn: ID) =>
        `${Routes.project.environment.features(proj, env)}/${encodeRouteParam(fqn)}`,
      datasets: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/datasets`,
      tiles: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/tiles`,
      /** The tiles page with one tile series selected, optionally opening a drawer tab. */
      tileSeries: (proj: ID, env: ID, materializationKeyHash: string, drawerTab?: "gaps") =>
        `${Routes.project.environment.tiles(proj, env)}?${new URLSearchParams({
          [DynamicQueryParam.TILES_SELECTED_KEY]: materializationKeyHash,
          // The key useTypesafeQueryState reads for the namespaced drawer tab.
          ...(drawerTab == null
            ? {}
            : { [`${TILES_DRAWER_QUERY_NAMESPACE}.${DynamicQueryParam.VIEW}`]: drawerTab }),
        })}`,
      dataset: (proj: ID, env: ID, datasetId: ID) =>
        `${Routes.project.environment.root(proj, env)}/datasets/${encodeRouteParam(datasetId)}`,
      datasetRevision: (proj: ID, env: ID, datasetId: ID, datasetRevisionId: ID) =>
        `${Routes.project.environment.root(
          proj,
          env,
        )}/datasets/${encodeRouteParam(datasetId)}/revisions/${encodeRouteParam(datasetRevisionId)}`,
      dashboards: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/dashboards`,
      dashboardDetail: (proj: ID, env: ID, dashboardId: ID) =>
        `${Routes.project.environment.dashboards(proj, env)}/${encodeRouteParam(dashboardId)}`,
      dashboardTv: (proj: ID, env: ID, dashboardId: ID) =>
        `${Routes.project.environment.dashboardDetail(proj, env, dashboardId)}/tv`,
      // Static pages that shadow the [dashboardId] route, so these slugs are reserved ids.
      kubernetesPodsOverview: (proj: ID, env: ID) =>
        `${Routes.project.environment.dashboards(proj, env)}/kubernetes-pods-overview`,
      kubernetesNodesOverview: (proj: ID, env: ID) =>
        `${Routes.project.environment.dashboards(proj, env)}/kubernetes-nodes-overview`,
      detailedPerformanceMetrics: (proj: ID, env: ID) =>
        `${Routes.project.environment.dashboards(proj, env)}/detailed-performance-metrics`,
      bloomFiltersOverview: (proj: ID, env: ID) =>
        `${Routes.project.environment.dashboards(proj, env)}/bloom-filters-overview`,
      models: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/models`,
      modelDeployments: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/model-deployments`,
      modelDeployment: (proj: ID, env: ID, modelDeploymentId: ID) =>
        `${Routes.project.environment.modelDeployments(proj, env)}/${encodeRouteParam(modelDeploymentId)}`,
      model: (proj: ID, env: ID, modelId: ID) =>
        `${Routes.project.environment.models(proj, env)}/${encodeRouteParam(modelId)}`,
      modelVersion: (proj: ID, env: ID, modelId: ID, modelVersion: ID) =>
        `${Routes.project.environment.model(proj, env, modelId)}/versions/${encodeRouteParam(modelVersion)}`,
      trainingRunsV2: (proj: ID, env: ID) =>
        `${Routes.project.environment.models(proj, env)}/training-runs-v2`,
      trainingRunV2: (proj: ID, env: ID, trainingRunId: ID) =>
        `${Routes.project.environment.models(proj, env)}/training-runs-v2/${encodeRouteParam(trainingRunId)}`,
      modelArtifact: (proj: ID, env: ID, modelArtifactId: ID) =>
        `${Routes.project.environment.models(proj, env)}/artifacts/${encodeRouteParam(modelArtifactId)}`,
      allMetaQueriesAdminOnly: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/meta-queries`,
      namedQueries: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/queries`,
      namedQuery: (proj: ID, env: ID, queryName: ID, version?: ID) =>
        `${Routes.project.environment.root(proj, env)}/queries/named/${encodeRouteParam(
          queryName,
        )}${version != null ? `?ver=${encodeRouteParam(version)}` : ""}`,
      metaQuery: (proj: ID, env: ID, metaQueryId: ID, queryName?: ID) =>
        `${Routes.project.environment.root(proj, env)}/queries/${encodeRouteParam(metaQueryId)}${
          queryName ? `?queryName=${encodeRouteParam(queryName)}` : ""
        }`,
      archivedMetaQueries: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/archived-queries`,
      metaQueryRuns: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/query-runs`,
      queryRun: (proj: ID, env: ID, queryRunId: ID) =>
        `${Routes.project.environment.root(proj, env)}/query-runs/${encodeRouteParam(queryRunId)}`,
      metaQueryRun: (
        proj: ID,
        env: ID,
        metaQueryRunId: ID,
        approximateTimestamp: Date,
        source?: string,
      ) =>
        `${Routes.project.environment.root(
          proj,
          env,
        )}/query-runs/${encodeRouteParam(metaQueryRunId)}?ts=${approximateTimestamp.getTime()}${
          source ? `&${StaticQueryParam.NAVIGATION_SOURCE}=${encodeRouteParam(source)}` : ""
        }`,
      monitor: (proj: ID, env: ID, monitorId: ID) =>
        `${Routes.project.environment.root(proj, env)}/monitors/${encodeRouteParam(monitorId)}`,
      newMonitor: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/monitors/new`,
      offlineQueries: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/offline-queries`,
      offlineQueryUtilization: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/offline-queries/utilization`,
      offlineQueryResourceGroupUtilization: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/offline-queries/resource-group-utilization`,
      offlineQuery: (proj: ID, env: ID, operationId: ID, source?: string) =>
        `${Routes.project.environment.root(proj, env)}/offline-queries/${encodeRouteParam(operationId)}${
          source ? `?${[StaticQueryParam.NAVIGATION_SOURCE]}=${encodeRouteParam(source)}` : ""
        }`,
      storage: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/storage`,
      scriptTasks: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/tasks`,
      scriptTask: (proj: ID, env: ID, scriptTaskId: ID) =>
        `${Routes.project.environment.root(proj, env)}/tasks/${encodeRouteParam(scriptTaskId)}`,
      queryPlans: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/query-plans`,
      queryPlan: (proj: ID, env: ID, planId: ID) =>
        `${Routes.project.environment.root(proj, env)}/query-plans/${encodeRouteParam(planId)}`,
      traces: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/traces`,
      trace: (proj: ID, env: ID, traceId: ID) =>
        `${Routes.project.environment.root(proj, env)}/traces/${encodeRouteParam(traceId)}`,
      spans: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/spans`,
      spanSources: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/span-sources`,
      aiTraces: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/ai-traces`,
      aiTraceSession: (proj: ID, env: ID, sessionId: string) =>
        `${Routes.project.environment.aiTraces(proj, env)}/sessions/${encodeRouteParam(sessionId)}`,
      settings: (proj: ID, env: ID, view = "permissions", tab?: string | undefined) =>
        appendViewQueryParam(
          `${Routes.project.environment.root(proj, env)}/settings/${encodeRouteParam(view)}`,
          tab,
        ),
      settingsBucketsNew: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/settings/buckets/new`,
      kubePod: (proj: ID, env: ID, podName: string) =>
        `${Routes.project.environment.root(proj, env)}/kubernetes/pods/${encodeRouteParam(
          podName,
        )}`,
      kubeServiceAccount: (proj: ID, env: ID, serviceAccountName: string) =>
        `${Routes.project.environment.root(
          proj,
          env,
        )}/kubernetes/service-accounts/${encodeRouteParam(serviceAccountName)}`,
      kubeService: (proj: ID, env: ID, kubeServiceId: string) =>
        `${Routes.project.environment.root(proj, env)}/kubernetes/services/${encodeRouteParam(kubeServiceId)}`,
      kubeRoute: (proj: ID, env: ID, routeId: string) =>
        `${Routes.project.environment.root(proj, env)}/kubernetes/routes/${encodeRouteParam(
          routeId,
        )}`,
      kubeRoutingGraph: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/kubernetes/routes/graph`,
      kubeNode: (proj: ID, env: ID, nodeName: string) =>
        `${Routes.project.environment.root(proj, env)}/kubernetes/nodes/${encodeRouteParam(
          nodeName,
        )}`,
      kubeAutoscaler: (proj: ID, env: ID, autoscalerName: string) =>
        `${Routes.project.environment.root(proj, env)}/kubernetes/autoscalers/${encodeRouteParam(
          autoscalerName,
        )}`,
      kubeDeployment: (
        proj: ID,
        env: ID,
        deploymentName: string,
        namespace: string,
        clusterName: string,
      ) =>
        `${Routes.project.environment.root(proj, env)}/kubernetes/deployments/${encodeRouteParam(
          deploymentName,
        )}?namespace=${encodeRouteParam(namespace)}&clusterName=${encodeRouteParam(clusterName)}`,
      kubeStatefulSet: (
        proj: ID,
        env: ID,
        statefulSetName: string,
        namespace: string,
        clusterName: string,
      ) =>
        `${Routes.project.environment.root(proj, env)}/kubernetes/statefulsets/${encodeRouteParam(
          statefulSetName,
        )}?namespace=${encodeRouteParam(namespace)}&clusterName=${encodeRouteParam(clusterName)}`,
      kubeJob: (proj: ID, env: ID, jobName: string, namespace: string, clusterName: string) =>
        `${Routes.project.environment.root(proj, env)}/kubernetes/jobs/${encodeRouteParam(
          jobName,
        )}?namespace=${encodeRouteParam(namespace)}&clusterName=${encodeRouteParam(clusterName)}`,
      kubePersistentVolume: (proj: ID, env: ID, persistentVolumeName: string) =>
        `${Routes.project.environment.root(proj, env)}/kubernetes/persistent-volumes/${encodeRouteParam(
          persistentVolumeName,
        )}`,
      permissions: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/settings/permissions`,
      tokens: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/settings/tokens`,
      agents: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/agents`,
      agent: (proj: ID, env: ID, agentId: ID) =>
        `${Routes.project.environment.root(proj, env)}/agents/${encodeRouteParam(agentId)}`,
      jobs: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/jobs`,
      job: (proj: ID, env: ID, jobId: ID) =>
        `${Routes.project.environment.root(proj, env)}/jobs/${encodeRouteParam(jobId)}`,
      metadataCronJobs: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/metadata-cron-jobs`,
      metadataCronJob: (proj: ID, env: ID, scheduledJobId: ID) =>
        `${Routes.project.environment.root(proj, env)}/metadata-cron-jobs/${encodeRouteParam(scheduledJobId)}`,
      workflows: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/workflows`,
      workflow: (proj: ID, env: ID, workflowId: ID) =>
        `${Routes.project.environment.root(proj, env)}/workflows/${encodeRouteParam(workflowId)}`,
      secrets: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/secrets`,
      envVariables: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/environment-variables`,
      dataSources: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/data-sources`,
      onlineStore: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/data-sources/online-store`,
      onlineStoreKind: (proj: ID, env: ID, kind: string) =>
        `${Routes.project.environment.onlineStore(proj, env)}/edit?kind=${encodeRouteParam(
          kind.toLowerCase(),
        )}`,
      vectorDB: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/data-sources/vector-db`,
      vectorDBKind: (proj: ID, env: ID, kind: string) =>
        `${Routes.project.environment.vectorDB(proj, env)}/edit?kind=${encodeRouteParam(
          kind.toLowerCase(),
        )}`,
      mcpGateway: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/mcp-gateway`,
      mcpServers: (proj: ID, env: ID) => Routes.project.environment.mcpGateway(proj, env),
      // No query string: sign-in passes this path through `callbackUrl` unencoded.
      mcpConnectAccounts: (proj: ID, env: ID) =>
        `${Routes.project.environment.mcpGateway(proj, env)}/connect`,
      mcpServerDetail: (proj: ID, env: ID, serverName: string) =>
        `${Routes.project.environment.root(proj, env)}/data-sources/mcp-servers/${encodeRouteParam(serverName)}`,
      agentPolicies: (proj: ID, env: ID) =>
        `${Routes.project.environment.mcpGateway(proj, env)}?section=policies`,
      agentPolicyNew: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/agent-policies/new`,
      agentPolicyEdit: (proj: ID, env: ID, policyName: string) =>
        `${Routes.project.environment.root(proj, env)}/agent-policies/${encodeRouteParam(policyName)}`,
      agentTemplates: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/agent-templates`,
      agentTemplateNew: (proj: ID, env: ID) =>
        `${Routes.project.environment.agentTemplates(proj, env)}/new`,
      agentTemplateEdit: (proj: ID, env: ID, templateId: ID) =>
        `${Routes.project.environment.agentTemplates(proj, env)}/${encodeRouteParam(templateId)}`,
      agentBuilder: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/agent-builder`,
      evaluations: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/evaluations`,
      evaluationSuite: (proj: ID, env: ID, evaluationSuiteId: ID, tab?: "definitions" | "runs") =>
        appendViewQueryParam(
          `${Routes.project.environment.evaluations(proj, env)}/suite/${encodeRouteParam(evaluationSuiteId)}`,
          tab,
        ),
      evaluationNew: (proj: ID, env: ID) =>
        `${Routes.project.environment.evaluations(proj, env)}/new`,
      evaluation: (proj: ID, env: ID, evaluationId: ID) =>
        `${Routes.project.environment.evaluations(proj, env)}/${encodeRouteParam(evaluationId)}`,
      evaluationCompare: (proj: ID, env: ID, evaluationIds: ID[]) => {
        const params = new URLSearchParams({
          [DynamicQueryParam.EVALUATION_COMPARE]: evaluationIds.map(String).join(","),
        });
        return `${Routes.project.environment.evaluations(proj, env)}/compare?${params}`;
      },
      evaluationRun: (proj: ID, env: ID, evaluationId: ID, evaluationRunId: ID) => {
        const params = new URLSearchParams({
          [DynamicQueryParam.EVALUATION_RUN_ID]: String(evaluationRunId),
        });
        return `${Routes.project.environment.evaluation(proj, env, evaluationId)}?${params}`;
      },
      modelGateway: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/model-gateway`,
      routerRouting: (proj: ID, env: ID, tab?: RouterRoutingTab) =>
        `${Routes.project.environment.modelGateway(proj, env)}?section=routing${tab == null ? "" : `&routing.v=${encodeRouteParam(tab)}`}`,
      routerAccess: (proj: ID, env: ID, tab?: RouterAccessTab) =>
        `${Routes.project.environment.modelGateway(proj, env)}?section=access${tab == null ? "" : `&access.v=${encodeRouteParam(tab)}`}`,
      routerCreateKeyInPool: (proj: ID, env: ID, poolId: ID) =>
        `${Routes.project.environment.routerAccess(proj, env, "keys")}&createKey=1&usagePoolId=${encodeRouteParam(poolId)}`,
      routerTelemetry: (proj: ID, env: ID) =>
        `${Routes.project.environment.modelGateway(proj, env)}?section=exporters`,
      routerPlayground: (proj: ID, env: ID) =>
        `${Routes.project.environment.modelGateway(proj, env)}?section=playground`,
      // Detail routes have their own `?v=` state, independent of gateway section views.
      routerConnectionDetail: (
        proj: ID,
        env: ID,
        connectionId: ID,
        tab?: RouterConnectionDetailTab,
      ) =>
        appendViewQueryParam(
          `${Routes.project.environment.root(proj, env)}/model-gateway/connections/${encodeRouteParam(connectionId)}`,
          tab,
        ),
      routerRouteCreate: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/model-gateway/routes/new`,
      routerRouteEdit: (proj: ID, env: ID, routeId: ID) =>
        `${Routes.project.environment.root(proj, env)}/model-gateway/routes/${encodeRouteParam(routeId)}/edit`,
      routerRouteDetail: (proj: ID, env: ID, routeId: ID, tab?: RouterRouteDetailTab) =>
        appendViewQueryParam(
          `${Routes.project.environment.root(proj, env)}/model-gateway/routes/${encodeRouteParam(routeId)}`,
          tab,
        ),
      // Keyed by `key_hash`: it is what UpdateKey/RevokeKey take, and
      // `key_prefix` is only 8 hex characters with no uniqueness constraint
      // behind it, so it cannot address a key.
      routerKeyDetail: (proj: ID, env: ID, keyHash: ID, tab?: RouterKeyDetailTab) =>
        appendViewQueryParam(
          `${Routes.project.environment.root(proj, env)}/model-gateway/keys/${encodeRouteParam(keyHash)}`,
          tab,
        ),
      routerUsagePoolDetail: (proj: ID, env: ID, poolId: ID) =>
        `${Routes.project.environment.root(proj, env)}/model-gateway/usage-pools/${encodeRouteParam(poolId)}`,
      offlineStore: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/data-sources/offline-store`,
      offlineStoreNew: (proj: ID, env: ID) =>
        `${Routes.project.environment.offlineStore(proj, env)}/new`,
      offlineStoreConnection: (proj: ID, env: ID, connectionId: ID) =>
        `${Routes.project.environment.offlineStore(proj, env)}/${encodeRouteParam(connectionId)}`,
      featureExports: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/exports`,
      featureExport: (proj: ID, env: ID, exportId: ID) =>
        `${Routes.project.environment.root(proj, env)}/exports/${encodeRouteParam(exportId)}`,
      newDataSource: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/data-sources/new`,
      newDataSourceKind: (proj: ID, env: ID) =>
        `${Routes.project.environment.newDataSource(proj, env)}/kind`,
      newDataSourceConfigure: (proj: ID, env: ID, kind: string) =>
        `${Routes.project.environment.newDataSource(proj, env)}/configure?kind=${encodeRouteParam(
          kind.toLowerCase(),
        )}`,
      editDataSource: (proj: ID, env: ID, integrationId: ID, tab?: IntegrationDetailTab) =>
        appendViewQueryParam(
          `${Routes.project.environment.dataSources(proj, env)}/edit/${encodeRouteParam(integrationId)}`,
          tab,
        ),
      monitors: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/monitors`,
      incidents: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/incidents`,
      incident: (proj: ID, env: ID, incId: ID) =>
        `${Routes.project.environment.incidents(proj, env)}/${encodeRouteParam(incId)}`,
      alertConfig: (proj: ID, env: ID, tab?: string | undefined) =>
        appendViewQueryParam(`${Routes.project.environment.root(proj, env)}/alert-config`, tab),
      webhooks: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/webhooks`,
      globalMetrics: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/global-metrics`,
      metricChart: (proj: ID, env: ID, metricConfigId: ID) =>
        `${Routes.project.environment.root(proj, env)}/metrics/chart/${encodeRouteParam(metricConfigId)}`,
      metricsExplorer: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/metrics-explorer`,
      // Intentionally unlisted in ProjectNavbar.constants: a page to hand out, not to
      // discover. See pages/.../raw-metrics-explorer.tsx.
      rawMetricsExplorer: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/raw-metrics-explorer`,
      clickhouse: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/clickhouse`,
      featureStudio: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/studio`,
      dataExplorer: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/data-explorer/sql?${DynamicQueryParam.VIEW}=online`,
      dataExplorerOnline: (
        proj: ID,
        env: ID,
        metaQueryId?: string,
        rerunOperationId?: string,
        rerunTimestamp?: Date,
        // When set, the explorer pre-selects this branch so a rerun targets the branch's deployment
        // rather than the default one (used by the branch editor's live watches).
        branch?: string,
      ) => {
        const params = new URLSearchParams();
        params.set(DynamicQueryParam.VIEW, "online");
        if (metaQueryId) params.set(DynamicQueryParam.QUERY_STRING, metaQueryId);
        if (rerunOperationId) {
          params.set(DynamicQueryParam.RERUN_OPERATION_ID, rerunOperationId);
          if (rerunTimestamp) {
            params.set(DynamicQueryParam.RERUN_TIMESTAMP, String(rerunTimestamp.getTime()));
          }
        }
        if (branch) params.set(DynamicQueryParam.BRANCH, branch);
        const qs = params.toString();
        return `${Routes.project.environment.root(proj, env)}/data-explorer/sql${
          qs ? `?${qs}` : ""
        }`;
      },
      dataExplorerOffline: (proj: ID, env: ID, offlineQueryId?: string) => {
        const params = new URLSearchParams();
        params.set(DynamicQueryParam.VIEW, "offline");
        if (offlineQueryId) params.set(DynamicQueryParam.QUERY_STRING, offlineQueryId);
        const qs = params.toString();
        return `${Routes.project.environment.root(proj, env)}/data-explorer/sql${
          qs ? `?${qs}` : ""
        }`;
      },
      dataExplorerSql: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/data-explorer/sql`,
      dataExplorerSqlHistory: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/sql/queries`,
      dataExplorerSqlHistoryQuery: (proj: ID, env: ID, queryId: ID) =>
        `${Routes.project.environment.root(proj, env)}/sql/queries/${encodeRouteParam(queryId)}`,
      dataframeRuns: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/dataframe-runs`,
      dataframeRun: (proj: ID, env: ID, dataframeRunId: ID) =>
        `${Routes.project.environment.root(proj, env)}/dataframe-runs/${encodeRouteParam(dataframeRunId)}`,
      dataLineage: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/data-lineage`,
      dataCatalog: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/data-catalog`,
      catalogOverview: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/catalog-overview`,
      admin: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/admin`,
      onlineStoreConfig: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/online-store-config`,
      adminNodes: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/admin-nodes`,
      adminBaseImages: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/admin-base-images`,
      adminNodeDetail: (proj: ID, env: ID, cluster: string, node: string, nodeUid?: string) =>
        `${Routes.project.environment.root(proj, env)}/admin-nodes/${encodeRouteParam(
          cluster,
        )}/${encodeRouteParam(node)}${nodeUid ? `?nodeUid=${encodeRouteParam(nodeUid)}` : ""}`,
      adminResourceGroupService: (proj: ID, env: ID, serviceKind: string, resourceGroup: string) =>
        `${Routes.project.environment.root(
          proj,
          env,
        )}/admin-nodes/resource-group-service?serviceKind=${encodeRouteParam(
          serviceKind,
        )}&resourceGroup=${encodeRouteParam(resourceGroup)}`,
      jobQueueConsumer: (proj: ID, env: ID, jobQueueConsumerId: ID) =>
        `${Routes.project.environment.root(
          proj,
          env,
        )}/job-queue-consumers/${encodeRouteParam(jobQueueConsumerId)}`,
      resourceGroupMetrics: (proj: ID, env: ID, resourceGroup: string) =>
        `${Routes.project.environment.root(proj, env)}/rgs/${encodeRouteParam(
          resourceGroup,
        )}/metrics`,
      resourceGroupQueryQueue: (proj: ID, env: ID, resourceGroup: string) =>
        `${Routes.project.environment.root(proj, env)}/rgs/${encodeRouteParam(
          resourceGroup,
        )}/query-queue`,
      resourceGroupJobs: (proj: ID, env: ID, resourceGroup: string, operationId?: ID) => {
        const base = `${Routes.project.environment.root(proj, env)}/rgs/${encodeRouteParam(
          resourceGroup,
        )}/jobs`;
        return operationId ? `${base}?operationId=${encodeRouteParam(operationId)}` : base;
      },
      mlCenter: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/agents`,
      observability: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/observability`,
      dataOverview: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/data`,
      computeHome: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/compute`,
      // The environment overview, which the root also renders unless it lands on Compute Home.
      overview: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/overview`,
      sandboxes: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/sandboxes`,
      sandbox: (proj: ID, env: ID, sandboxId: ID) =>
        `${Routes.project.environment.sandboxes(proj, env)}/${encodeRouteParam(sandboxId)}`,
      notebooks: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/notebooks`,
      notebook: (proj: ID, env: ID, notebookId: ID) =>
        `${Routes.project.environment.notebooks(proj, env)}/${encodeRouteParam(notebookId)}`,
      notebookKernels: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/notebook-kernels`,
      favorites: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/favorites`,
      volumes: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/volumes`,
      volume: (proj: ID, env: ID, volumeName: ID) =>
        `${Routes.project.environment.volumes(proj, env)}/${encodeRouteParam(volumeName)}`,
      volumeHtmlPreview: (
        proj: ID,
        env: ID,
        volumeName: ID,
        file: string,
        selector: { ref?: string; versionId?: bigint },
      ) => {
        const params = new URLSearchParams({ [DynamicQueryParam.VOLUME_FILE]: file });
        if (selector.versionId != null) {
          params.set(DynamicQueryParam.VOLUME_VERSION, selector.versionId.toString());
        } else if (selector.ref != null) {
          params.set(DynamicQueryParam.VOLUME_REF, selector.ref);
        }
        return `${Routes.project.environment.volume(proj, env, volumeName)}/preview?${params}`;
      },
      debugShell: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/debug-shell`,
      logs: {
        index: (proj: ID, env: ID, searchType?: string, search?: string) => {
          const queryString = fastCompact([
            searchType ? `lgst=${encodeRouteParam(searchType)}` : null,
            search ? `q=${encodeRouteParam(search)}` : null,
          ]).join("&");
          return `${Routes.project.environment.root(proj, env)}/logs${
            queryString ? `?${queryString}` : ""
          }`;
        },
        access: (proj: ID, env: ID, search?: string, clusterId?: string) => {
          const params = new URLSearchParams();
          if (search) params.set("q", search);
          if (clusterId) params.set("clusterId", clusterId);
          return `${Routes.project.environment.root(proj, env)}/logs/access-logs${
            params.size ? `?${params}` : ""
          }`;
        },
        audit: (proj: ID, env: ID) =>
          `${Routes.project.environment.root(proj, env)}/logs/audit-logs`,
      },
      errors: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/errors`,
      aggregations: (proj: ID, env: ID) => Routes.project.environment.runs(proj, env),
      aggregationBackfillJobs: (proj: ID, env: ID) =>
        Routes.project.environment.runs(proj, env, "history"),
      aggregateBackfillJob: (
        proj: ID,
        env: ID,
        jobId: ID,
        source: string | undefined = undefined,
      ) => {
        const queryString = source
          ? `${[StaticQueryParam.NAVIGATION_SOURCE]}=${encodeRouteParam(source)}`
          : undefined;
        return `${Routes.project.environment.root(proj, env)}/aggregations/${encodeRouteParam(jobId)}${
          queryString ? `?${queryString}` : ""
        }`;
      },
      cronAggBackfills: (proj: ID, env: ID) => Routes.project.environment.runs(proj, env),
      cronAggregateBackfillJob: (proj: ID, env: ID, cronAggBackfillName: ID, tab?: string) =>
        appendViewQueryParam(
          `${Routes.project.environment.root(
            proj,
            env,
          )}/scheduled-aggregations/${encodeRouteParam(cronAggBackfillName)}`,
          tab,
        ),
      cronAggregateBackfillByPlanHash: (proj: ID, env: ID, cronAggPlanHash: ID) =>
        `${Routes.project.environment.root(
          proj,
          env,
        )}/scheduled-aggregations-by-plan-hash/${encodeRouteParam(cronAggPlanHash)}`,
      cloudOverview: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/cloud-overview`,
      kubernetes: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/kubernetes`,
      kubeWorkloadManagers: (proj: ID, env: ID) =>
        appendViewQueryParam(
          `${Routes.project.environment.root(proj, env)}/kubernetes`,
          "workload-managers",
        ),
      kubeEvents: (proj: ID, env: ID, search?: string) => {
        const suffix = search
          ? `?${DynamicQueryParam.QUERY_STRING}=${encodeRouteParam(search)}`
          : "";
        return `${Routes.project.environment.root(proj, env)}/kube-events${suffix}`;
      },
      nodepools: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/nodepools`,
      metricsDatabase: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/metrics-database`,
      backgroundPersistence: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/background-persistence`,
      workflowOrchestratorEngine: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/workflow-orchestrator-engine`,
      resources: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/resources`,
      resourcesResourceGroup: (proj: ID, env: ID, resourceGroup: string) =>
        `${Routes.project.environment.root(proj, env)}/resources/${encodeRouteParam(
          resourceGroup,
        )}`,
      resourcesResourceGroupService: (
        proj: ID,
        env: ID,
        resourceGroup: string,
        serviceKind: string,
      ) =>
        `${Routes.project.environment.root(proj, env)}/resources/${encodeRouteParam(resourceGroup)}/${encodeRouteParam(serviceKind)}`,
      settingsResourcesResourceGroup: (proj: ID, env: ID, resourceGroup: string) =>
        `${Routes.project.environment.root(proj, env)}/settings/resources/${encodeRouteParam(resourceGroup)}`,
      settingsResourcesResourceGroupService: (
        proj: ID,
        env: ID,
        resourceGroup: string,
        serviceKind: string,
      ) =>
        `${Routes.project.environment.root(
          proj,
          env,
        )}/settings/resources/${encodeRouteParam(resourceGroup)}/${encodeRouteParam(serviceKind)}`,
      artifactRepositories: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/artifact-repositories`,
      workflowOrchestrator: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/workflow-orchestrator`,
      workflowOrchestratorWorkflow: (
        proj: ID,
        env: ID,
        namespace: ID,
        workflowId: ID,
        runId?: ID,
      ) =>
        `${Routes.project.environment.workflowOrchestrator(proj, env)}/${encodeRouteParam(
          workflowId,
        )}?ns=${encodeRouteParam(namespace)}${runId ? `&runId=${encodeRouteParam(runId)}` : ""}`,
      argoWorkflows: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/argo-workflows`,
      argoWorkflow: (proj: ID, env: ID, workflowName: ID) =>
        `${Routes.project.environment.argoWorkflows(proj, env)}/${encodeRouteParam(workflowName)}`,
      customImageBuilds: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/images`,
      customImageBuild: (proj: ID, env: ID, contentHash: ID) =>
        `${Routes.project.environment.customImageBuilds(proj, env)}/${encodeRouteParam(
          contentHash,
        )}`,
      buildProfile: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/build-profile`,
      scalingGroups: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/scaling-groups`,
      scalingGroup: (proj: ID, env: ID, scalingGroupId: ID) =>
        `${Routes.project.environment.scalingGroups(proj, env)}/${encodeRouteParam(scalingGroupId)}`,
      // Name links share the same route. The detail page chooses the get-by-id
      // or get-by-name request from the identifier format.
      scalingGroupByName: (proj: ID, env: ID, scalingGroupName: ID) =>
        `${Routes.project.environment.scalingGroups(proj, env)}/${encodeRouteParam(
          scalingGroupName,
        )}`,
      scalingGroupRevision: (
        proj: ID,
        env: ID,
        scalingGroupIdOrName: ID,
        scalingGroupRevisionId: ID,
      ) =>
        `${Routes.project.environment.scalingGroup(
          proj,
          env,
          scalingGroupIdOrName,
        )}/revisions/${encodeRouteParam(scalingGroupRevisionId)}`,
      containers: (proj: ID, env: ID) => `${Routes.project.environment.root(proj, env)}/containers`,
      container: (proj: ID, env: ID, containerId: ID) =>
        `${Routes.project.environment.containers(proj, env)}/${encodeRouteParam(containerId)}`,
      containerSnapshots: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/container-snapshots`,
      containerSnapshot: (proj: ID, env: ID, snapshotId: ID) =>
        `${Routes.project.environment.containerSnapshots(proj, env)}/${encodeRouteParam(snapshotId)}`,
      storedArtifact: (proj: ID, env: ID, artifactId: ID) =>
        `${Routes.project.environment.root(proj, env)}/artifacts/${encodeRouteParam(artifactId)}`,
      externalFunctionVersion: (proj: ID, env: ID, versionId: ID) =>
        `${Routes.project.environment.root(proj, env)}/external-function-versions/${encodeRouteParam(versionId)}`,
      externalFunctions: (proj: ID, env: ID) =>
        `${Routes.project.environment.root(proj, env)}/external-functions`,
      externalFunctionNew: (proj: ID, env: ID) =>
        `${Routes.project.environment.externalFunctions(proj, env)}/new`,
      externalFunction: (proj: ID, env: ID, externalFunctionName: ID) =>
        `${Routes.project.environment.externalFunctions(proj, env)}/${encodeRouteParam(
          externalFunctionName,
        )}`,
    },
  },
};
